import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import hubspotApi from "../../../api/hubspot-api";

/**
 * Hook to get HubSpot integration status for the current organization
 */
export const useHubspotStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await hubspotApi.getStatus(organizationId);
      setStatus(response?.data || null);
    } catch (err) {
      // 404 means not connected yet, which is a valid state
      if (err?.message?.includes('404')) {
        setStatus(null);
      } else {
        setError(err?.message || "Failed to fetch HubSpot status");
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return { status, loading, error, refetch: fetchStatus };
};

/**
 * Hook to link organization to HubSpot
 */
export const useHubspotLinkOrg = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const linkOrg = useCallback(
    async (hubspotHubId) => {
      if (!organizationId) {
        throw new Error("Organization ID is required");
      }

      setLoading(true);
      setError(null);
      try {
        const response = await hubspotApi.linkOrg(organizationId, hubspotHubId);
        return response?.data;
      } catch (err) {
        const errorMessage = err?.message || "Failed to link organization";
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [organizationId]
  );

  return { linkOrg, loading, error };
};

/**
 * Hook to get HubSpot install URL
 */
export const useHubspotInstallUrl = () => {
  const [installUrl, setInstallUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchInstallUrl = useCallback(async () => {
    if (!organizationId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await hubspotApi.getInstallUrl(organizationId);
      setInstallUrl(response?.data?.installUrl || response?.installUrl || null);
    } catch (err) {
      setError(err?.message || "Failed to fetch install URL");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  return { installUrl, loading, error, fetchInstallUrl };
};

/**
 * Hook to fetch HubSpot contacts for a specific company
 * @param {string} companyId - HubSpot company ID
 */
export const useHubspotCompanyContacts = (companyId) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const fetchContacts = useCallback(async () => {
    if (!organizationId || !companyId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await hubspotApi.getCompanyContacts(organizationId, companyId);
      setContacts(response?.data?.contacts || response?.contacts || []);
    } catch (err) {
      let errorMessage = "Failed to fetch contacts";

      if (err?.message?.includes('404')) {
        errorMessage = "HubSpot integration not installed yet";
      } else if (err?.message?.includes('401') || err?.message?.includes('403')) {
        errorMessage = "Not authorized to access HubSpot data";
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [organizationId, companyId]);

  useEffect(() => {
    if (companyId) {
      fetchContacts();
    }
  }, [companyId, fetchContacts]);

  return { contacts, loading, error, refetch: fetchContacts };
};

/**
 * Helper function to fetch HubSpot company contacts (non-hook version)
 * Useful for one-off calls outside of React components
 */
export const fetchHubspotCompanyContacts = async ({ organizationId, companyId }) => {
  if (!organizationId || !companyId) {
    throw new Error("organizationId and companyId are required");
  }

  try {
    const response = await hubspotApi.getCompanyContacts(organizationId, companyId);
    return {
      contacts: response?.data?.contacts || response?.contacts || [],
      error: null,
    };
  } catch (err) {
    let errorMessage = "Failed to fetch contacts";

    if (err?.message?.includes('404')) {
      errorMessage = "HubSpot integration not installed yet";
    } else if (err?.message?.includes('401') || err?.message?.includes('403')) {
      errorMessage = "Not authorized to access HubSpot data";
    } else if (err?.message) {
      errorMessage = err.message;
    }

    return {
      contacts: [],
      error: errorMessage,
    };
  }
};
